import React, { useEffect, useRef } from 'react';
import { Terminal } from 'xterm';
import { FitAddon } from 'xterm-addon-fit';
import { WebLinksAddon } from '@xterm/addon-web-links';
import { WebglAddon } from '@xterm/addon-webgl';
import 'xterm/css/xterm.css';

interface TerminalModalProps {
  sessionId: string;
  onClose: () => void;
}

export const TerminalModal: React.FC<TerminalModalProps> = ({ sessionId, onClose }) => {
  const terminalRef = useRef<HTMLDivElement>(null);
  const terminalInstance = useRef<Terminal | null>(null);
  const wsRef = useRef<WebSocket | null>(null);

  useEffect(() => {
    if (!terminalRef.current) return;

    // Initialize terminal
    const term = new Terminal({
      cursorBlink: true,
      fontSize: 14,
      fontFamily: 'Menlo, Monaco, "Courier New", monospace',
      theme: {
        background: '#1a1a1a',
        foreground: '#ffffff',
        cursor: '#ffffff',
        black: '#000000',
        red: '#e06c75',
        green: '#98c379',
        yellow: '#f0c674',
        blue: '#61afef',
        magenta: '#c678dd',
        cyan: '#56b6c2',
        white: '#abb2bf',
        brightBlack: '#5c6370',
        brightRed: '#e06c75',
        brightGreen: '#98c379',
        brightYellow: '#f0c674',
        brightBlue: '#61afef',
        brightMagenta: '#c678dd',
        brightCyan: '#56b6c2',
        brightWhite: '#ffffff',
      },
    });

    // Add addons
    const fitAddon = new FitAddon();
    term.loadAddon(fitAddon);
    term.loadAddon(new WebLinksAddon());
    term.loadAddon(new WebglAddon());

    // Mount terminal
    term.open(terminalRef.current);
    fitAddon.fit();

    // Store terminal instance
    terminalInstance.current = term;

    // Connect to WebSocket
    const wsProtocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const wsHost = window.location.host;
    const ws = new WebSocket(`${wsProtocol}://${wsHost}/ws/ssh/${sessionId}`);

    // Handle WebSocket events
    ws.onopen = () => {
      term.write('\r\n\x1B[1;32mConnected to challenge environment\x1B[0m\r\n');
      // Send initial connection message
      ws.send(JSON.stringify({ type: 'connect', sessionId }));
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'error') {
          term.write(`\r\n\x1B[1;31mError: ${data.message}\x1B[0m\r\n`);
        } else if (data.type === 'output') {
          term.write(data.content);
        } else {
          term.write(event.data);
        }
      } catch (e) {
        // If not JSON, write raw data
      term.write(event.data);
      }
    };

    ws.onclose = (event) => {
      term.write(`\r\n\x1B[1;31mConnection closed (${event.code}): ${event.reason || 'No reason provided'}\x1B[0m\r\n`);
      // Attempt to reconnect if not closed normally
      if (event.code !== 1000) {
        setTimeout(() => {
          if (wsRef.current?.readyState === WebSocket.CLOSED) {
            term.write('\r\n\x1B[1;33mAttempting to reconnect...\x1B[0m\r\n');
            wsRef.current = new WebSocket(`${wsProtocol}://${wsHost}/ws/ssh/${sessionId}`);
          }
        }, 5000);
      }
    };

    ws.onerror = (error) => {
      term.write('\r\n\x1B[1;31mConnection error. Please try refreshing the page.\x1B[0m\r\n');
      console.error('WebSocket error:', error);
    };

    // Handle terminal input
    term.onData((data) => {
      if (ws.readyState === WebSocket.OPEN) {
        try {
          ws.send(JSON.stringify({ type: 'input', content: data }));
        } catch (e) {
          console.error('Error sending data:', e);
          term.write('\r\n\x1B[1;31mError sending data to server\x1B[0m\r\n');
        }
      }
    });

    // Store WebSocket instance
    wsRef.current = ws;

    // Handle window resize
    const handleResize = () => {
      fitAddon.fit();
    };
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      ws.close();
      term.dispose();
    };
  }, [sessionId]);

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50">
      <div className="bg-gray-900 rounded-lg shadow-xl w-full max-w-4xl h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <h3 className="text-lg font-semibold text-white">Challenge Terminal</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            Close
          </button>
        </div>
        <div className="flex-1 p-4">
          <div ref={terminalRef} className="w-full h-full" />
        </div>
      </div>
    </div>
  );
}; 